import { lazy, Suspense, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeftRight } from "lucide-react";
import { allZones, browserZone, modernZone, nowInZone, prettyZone, searchZones, zonedToUtc, offsetLabel } from "./tz";
import { friendlyZone, parseShare, DEFAULT_WORK, type WorkHours } from "./tzPlan";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { toast } from "sonner";
import { toCsv } from "@/lib/csv";
import { useProject } from "@/lib/workflow/useProject";
import { sortSessions } from "@/lib/workflow/sessionOps";
import { agendaLines, agendaTable, convertSession, dayShiftLabel } from "./agendaZones";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { useShareGate } from "@/hooks/useShareGate";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Converts a time from one time zone to another, useful for scheduling a call or meeting with someone in a different part of the world without doing the math yourself.`,
  how: `Enter the time and select the source and target time zones, and the converted time is calculated instantly, accounting for daylight saving time where applicable. Below the result, the meeting planner shows the day hour by hour in every zone with working hours shaded, the next clock change for each zone, a share link and a calendar file. If your project has an event agenda, it is shown below in the same zones, with a marker where the date changes, ready to copy or download.`,
  tip: `When scheduling across time zones that don't both observe daylight saving time at the same time of year, double-check the date too, not just the time — the offset between two zones can change by an hour depending on the season.`,
};

const TimezonePlanner = lazy(() => import("./TimezonePlanner"));

const ZONES = allZones();

function ZonePicker({ value, onChange, label }: { value: string; onChange: (z: string) => void; label: string }) {
  const [q, setQ] = useState("");
  const now = Date.now();
  const list = useMemo(() => searchZones(ZONES, q, value), [q, value]);
  const noMatch = q.trim() !== "" && list.length === 1 && list[0] === value && !searchZones(ZONES, q).length;
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input placeholder="Search city, country or zone (e.g. Mumbai, IST)" value={q} onChange={e => setQ(e.target.value)} aria-label={`${label} search`} />
      {noMatch && <p className="text-xs text-muted-foreground" role="status">No time zone matches "{q.trim()}". Try a city, a country or an abbreviation such as IST.</p>}
      <select aria-label={label} value={value} onChange={e => onChange(e.target.value)} className="w-full border rounded-md px-3 py-2 bg-background text-sm">
        {list.map(z => <option key={z} value={z}>{friendlyZone(now, z).text}</option>)}
      </select>
      <p className="text-xs text-muted-foreground" data-testid="tz-iana">Zone ID: {value}</p>
    </div>
  );
}

export default function TimezoneConverter() {
  const mine = browserZone();
  // A shared link (?z=zone,zone&t=local time&w=9-17) opens with exactly that plan. Anything that does not check out is ignored.
  const shared = useMemo(() => (typeof window === "undefined" ? null : parseShare(window.location.search)), []);
  const sharedZones = shared ? shared.zones.map((z) => (ZONES.includes(z) ? z : modernZone(z))) : null;
  const [fromTz, setFromTz] = useState(sharedZones?.[0] ?? (ZONES.includes(mine) ? mine : "America/New_York"));
  const [toTz, setToTz] = useState(sharedZones?.[1] ?? (mine === "Europe/London" ? "America/New_York" : "Europe/London"));
  const [time, setTime] = useState(() => shared?.time ?? nowInZone(ZONES.includes(mine) ? mine : "America/New_York"));
  const [extra, setExtra] = useState<string[]>(sharedZones ? sharedZones.slice(2) : []);
  const [work, setWork] = useState<WorkHours>(shared?.work ?? DEFAULT_WORK);

  const result = useMemo(() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(time);
    if (!m) return null;
    const [y, mo, d, h, mi] = m.slice(1).map(Number);
    try {
      const z = zonedToUtc(y, mo, d, h, mi, fromTz);
      const show = (zone: string) => new Date(z.utc).toLocaleString(undefined, { timeZone: zone, dateStyle: "full", timeStyle: "short" });
      return { z, main: show(toTz), mainOffset: offsetLabel(z.utc, toTz), others: extra.map((zone) => ({ zone, text: show(zone), off: offsetLabel(z.utc, zone) })) };
    } catch { return null; }
  }, [time, fromTz, toTz, extra]);

  const { blocked } = useLogToolUsage(!!result, 3000, { ownActions: true });
  const { gateShare } = useShareGate();

  const [project, store] = useProject();
  const sessions = useMemo(() => sortSessions(project.data.sessions), [project.data.sessions]);
  const agendaZones = useMemo(() => Array.from(new Set([toTz, ...extra])), [toTz, extra]);
  const projectZone = ZONES.includes(project.timeZone) ? project.timeZone : "UTC";
  const copyAgenda = () => gateShare(async () => {
    try {
      await navigator.clipboard.writeText([`${project.name} (times in ${prettyZone(projectZone)} unless noted)`, "", ...agendaLines(sessions, projectZone, agendaZones)].join("\n"));
      toast.success("Agenda copied to clipboard");
      return true;
    } catch { toast.error("Couldn't copy. Select the text and copy it by hand."); return false; }
  });
  const downloadAgenda = () => {
    const t = agendaTable(sessions, projectZone, agendaZones);
    const cols = t.header.map((h, i) => ({ key: i, header: h }));
    const rows = t.rows.map((r) => Object.fromEntries(r.map((v, i) => [i, v])));
    downloadBlob(new Blob([toCsv(rows as Record<number, string>[], cols)], { type: "text/csv;charset=utf-8" }), "agenda-time-zones.csv");
    toast.success("Agenda downloaded");
  };

  const changeFrom = (z: string) => {
    // Keep the same instant on screen when the From zone changes? No: the typed wall-clock time stays, now read in the new zone.
    setFromTz(z);
  };
  const swap = () => { setFromTz(toTz); setToTz(fromTz); };

  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 items-start">
        <div className="space-y-3">
          <ZonePicker label="From Timezone" value={fromTz} onChange={changeFrom} />
          <Input type="datetime-local" value={time} onChange={e => setTime(e.target.value)} aria-label="Date and time" />
          <Button variant="outline" size="sm" onClick={() => setTime(nowInZone(fromTz))}>Use the current time</Button>
        </div>
        <Button variant="ghost" size="icon" onClick={swap} aria-label="Swap time zones" className="md:mt-8"><ArrowLeftRight className="h-4 w-4" /></Button>
        <div className="space-y-3">
          <ZonePicker label="To Timezone" value={toTz} onChange={setToTz} />
          <div className="p-4 rounded-lg border bg-card" data-testid="tz-result">
            <p className="text-lg font-medium">{blocked || !result ? "—" : result.main}</p>
            {result && !blocked && <p className="text-xs text-muted-foreground mt-1">{friendlyZone(result.z.utc, toTz).text} · {toTz}</p>}
          </div>
        </div>
      </div>
      {result?.z.kind === "gap" && !blocked && (
        <NoticeBox className="mt-4 text-sm" testId="tz-gap">That time does not exist in {prettyZone(fromTz)} on that date: the clocks jump forward. It was read using the offset from before the jump, so it is shown as {nowInZone(fromTz, result.z.utc).split("T")[1]} on the same day.</NoticeBox>
      )}
      {result?.z.kind === "ambiguous" && !blocked && (
        <NoticeBox className="mt-4 text-sm" testId="tz-ambiguous">That time happens twice in {prettyZone(fromTz)} on that date (the clocks go back). The first occurrence is shown.</NoticeBox>
      )}
      <div className="mt-6 space-y-2">
        <Label>Also show the same moment in</Label>
        <div className="flex gap-2 flex-wrap items-center">
          <select aria-label="Add another time zone" value="" onChange={e => { if (e.target.value && extra.length < 6 && !extra.includes(e.target.value)) setExtra([...extra, e.target.value]); }} className="border rounded-md px-3 py-2 bg-background text-sm max-w-[260px]">
            <option value="">Add a zone…</option>
            {ZONES.map(z => <option key={z} value={z}>{prettyZone(z)}</option>)}
          </select>
        </div>
        {result && !blocked && result.others.map(o => (
          <div key={o.zone} className="flex items-center justify-between gap-3 p-3 rounded-lg border text-sm">
            <span><strong>{friendlyZone(result.z.utc, o.zone).title}</strong> ({friendlyZone(result.z.utc, o.zone).detail}): {o.text}</span>
            <Button size="sm" variant="ghost" aria-label={`Remove ${o.zone}`} onClick={() => setExtra(extra.filter(z => z !== o.zone))}>Remove</Button>
          </div>
        ))}
      </div>
      {shared && (
        <NoticeBox className="mt-4 text-sm" role="status" testId="tz-shared">Opened from a shared link. The time {shared.time.replace("T", " ")} is read in {prettyZone(fromTz)}. Change anything above to make your own plan.</NoticeBox>
      )}
      <Suspense fallback={null}>
        <TimezonePlanner zones={Array.from(new Set([fromTz, toTz, ...extra]))} time={time} utc={result ? result.z.utc : null} work={work} onWork={setWork} onPick={setTime} />
      </Suspense>
      {sessions.length > 0 && (
        <div className="mt-8 space-y-3" data-testid="tz-agenda">
          <div>
            <p className="text-sm font-medium">Your event agenda in these time zones</p>
            <p className="text-xs text-muted-foreground">Uses the zones chosen above (To timezone and Also show). Agenda times are read in:</p>
            <select aria-label="Agenda time zone" value={projectZone} onChange={(e) => store.set((p) => ({ ...p, timeZone: e.target.value }))} className="mt-1 border rounded-md px-3 py-2 bg-background text-sm max-w-full">
              {ZONES.map((z) => <option key={z} value={z}>{prettyZone(z)}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-muted-foreground border-b">
                <th className="p-2">Session</th><th className="p-2">{prettyZone(projectZone)}</th>
                {agendaZones.map((z) => <th key={z} className="p-2">{prettyZone(z)}</th>)}
              </tr></thead>
              <tbody>
                {sessions.map((x) => (
                  <tr key={x.id} className="border-b last:border-0 align-top">
                    <td className="p-2 font-medium">{x.title}<div className="text-xs font-normal text-muted-foreground">{x.day}</div></td>
                    <td className="p-2 whitespace-nowrap">{x.end ? `${x.start}-${x.end}` : x.start}</td>
                    {agendaZones.map((z) => {
                      const c = convertSession(x, projectZone, z);
                      return <td key={z} className="p-2 whitespace-nowrap">{c ? <>{c.end ? `${c.start}-${c.end}` : c.start}{c.dayShift !== 0 && <span className="ml-1 text-xs text-amber-600">{dayShiftLabel(c.dayShift)}</span>}{c.note && <span className="ml-1 text-xs text-amber-600" title="This time does not exist or happens twice on that date">!</span>}</> : "?"}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={copyAgenda}>Copy as text</Button>
            <Button variant="outline" size="sm" onClick={downloadAgenda}>Download CSV</Button>
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
